// ZoteroImportDemo.jsx
// Library -> Import from Zotero â live HTML/CSS/JS via React + Framer Motion
// (no GIF/video).
//
// Journey (loops forever, starts once scrolled into view):
//   1. LIBRARY  â the "My Uploads" table; the cursor clicks "Import from Zotero"
//   2. ZOTERO   â a Zotero dialog opens (Fetch Papers From Library / Fetch
//                 Collections); Fetch -> loading -> a checkbox list of PDFs
//   3. IMPORT   â the cursor ticks a couple of items, clicks Import Selected,
//                 the dialog closes and the picked papers appear as new rows
//
// No sidebar. Fills the full width of its container. Includes a cursor.
//
// Deps: framer-motion, lucide-react   ->  npm install framer-motion
// Usage: <ZoteroImportDemo />

import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import useDemoInView from "./useDemoInView";
import {
  Link as LinkIcon,
  Upload,
  BookOpen,
  Bookmark,
  Search,
  Trash2,
  ArrowUpRight,
  Check,
  FileText,
  Layers,
  Library as LibraryIcon,
} from "lucide-react";

// ---- Brand colors ----
const C = {
  blue: "#2f6df6",
  teal: "#0b8275",
  green: "#16a34a",
  red: "#e0524d",
  zoteroRed: "#c1272d",
  ink: "#1e293b",
  slate: "#334155",
  body: "#475569",
  muted: "#5C6B80",
  hint: "#5C6B80",
  field: "#f5f6f8",
  borderSoft: "#eef0f4",
  border: "#e0e3ea",
  cardBorder: "#e8eaef",
};

const EXISTING = [
  { icon: "link", t: "Limitations of large language models in medical applications", y: "", a: "", src: false },
  { icon: "pdf", t: "The study of boundaries in the social sciences", y: "2002", a: "M Lamont, V Molâ¦", src: true },
  { icon: "pdf", t: "Llm for test script generation and migration: Challenges, capabilities, and opportunities", y: "â", a: "â", src: true },
  { icon: "pdf", t: "LLM-in-the-loop: Leveraging large language model for thematic analysis", y: "â", a: "â", src: true },
  { icon: "pdf", t: "pnabl935.pdf", y: "â", a: "â", src: true },
];
const ZITEMS = ["2310.15100.pdf", "2309.13574.pdf", "2310.14724.pdf", "2310.00789.pdf", "2308.08155.pdf", "2307.10719.pdf"];
const IMPORT_IDX = [0, 1];

function PdfBadge() {
  return <span style={{ fontSize: 6.5, fontWeight: 700, color: "#fff", background: C.zoteroRed, borderRadius: 3, padding: 3 }}>PDF</span>;
}

function Row({ r, n, isNew }) {
  return (
    <motion.div
      initial={isNew ? { opacity: 0, y: 10 } : false}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      style={{ display: "flex", alignItems: "center", gap: 6, padding: "11px 12px", borderBottom: `1px solid #f1f3f7`, background: isNew ? "#f0f7ff" : "transparent" }}
    >
      <span style={{ width: 22 }}><span style={styles.ck} /></span>
      <span style={{ width: 18, fontSize: 10, color: C.body }}>{n}</span>
      <span style={{ flex: 2.4, display: "flex", alignItems: "center", gap: 7, fontSize: 10, color: C.blue, lineHeight: 1.3 }}>
        {r.icon === "link" ? <LinkIcon size={13} color={C.blue} /> : <PdfBadge />}
        <span>{r.t}</span>
      </span>
      <span style={{ width: 44, fontSize: 9.5, color: r.y && r.y !== "â" ? C.body : C.muted }}>{r.y || "â"}</span>
      <span style={{ flex: 1, fontSize: 9.5, color: r.a && r.a !== "â" ? C.body : C.muted }}>{r.a || "â"}</span>
      <span style={{ width: 44, textAlign: "center" }}>{r.src && <ArrowUpRight size={12} color={C.blue} />}</span>
      <span style={{ width: 60, fontSize: 9, color: C.body }}>29-07-20â¦</span>
      <span style={{ width: 52, textAlign: "center" }}>
        <span style={styles.trashBtn}><Trash2 size={11} color={C.red} /></span>
      </span>
      <span style={{ width: 66 }}><span style={styles.notAdded}>Not Added</span></span>
    </motion.div>
  );
}

export default function ZoteroImportDemo() {
  const rootRef = useRef(null);
  const inView = useDemoInView(rootRef, { once: true, margin: "-80px" });

  const [rows, setRows] = useState(EXISTING);
  const [newFrom, setNewFrom] = useState(EXISTING.length);
  const [modalOpen, setModalOpen] = useState(false);
  const [fetchView, setFetchView] = useState("buttons"); // 'buttons' | 'loading' | 'list'
  const [checked, setChecked] = useState([]);
  const [zbtnPressed, setZbtnPressed] = useState(false);
  const [fetchPressed, setFetchPressed] = useState(false);
  const [importPressed, setImportPressed] = useState(false);

  const [cursor, setCursor] = useState({ x: 0, y: 0, click: false });
  const zbtnRef = useRef(null);
  const fetchRef = useRef(null);
  const importRef = useRef(null);
  const itemRefs = useRef([]);

  useEffect(() => {
    if (!inView) return;
    let alive = true;
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    const moveTo = (ref) => {
      const frame = rootRef.current;
      const el = ref && ref.current;
      if (!frame || !el) return;
      const fr = frame.getBoundingClientRect();
      const er = el.getBoundingClientRect();
      setCursor((c) => ({ ...c, x: er.left - fr.left + er.width / 2, y: er.top - fr.top + er.height / 2, click: false }));
    };
    const clickPulse = async () => {
      setCursor((c) => ({ ...c, click: true }));
      await sleep(280);
      setCursor((c) => ({ ...c, click: false }));
    };

    const run = async () => {
      while (alive) {
        // reset
        setRows(EXISTING);
        setNewFrom(EXISTING.length);
        setModalOpen(false);
        setFetchView("buttons");
        setChecked([]);
        setZbtnPressed(false);
        setFetchPressed(false);
        setImportPressed(false);
        setCursor({ x: 400, y: 400, click: false });
        await sleep(900);
        if (!alive) return;

        // click Import from Zotero
        moveTo(zbtnRef);
        await sleep(700);
        await clickPulse();
        setZbtnPressed(true);
        await sleep(160);
        setZbtnPressed(false);
        setModalOpen(true);
        await sleep(700);
        if (!alive) return;

        // click Fetch Papers From Library
        moveTo(fetchRef);
        await sleep(650);
        await clickPulse();
        setFetchPressed(true);
        await sleep(160);
        setFetchPressed(false);
        setFetchView("loading");
        await sleep(1300);
        setFetchView("list");
        await sleep(700);
        if (!alive) return;

        // check items
        for (let k = 0; k < IMPORT_IDX.length; k++) {
          if (!alive) return;
          moveTo({ current: itemRefs.current[IMPORT_IDX[k]] });
          await sleep(550);
          await clickPulse();
          setChecked((c) => [...c, IMPORT_IDX[k]]);
          await sleep(300);
        }

        // import
        moveTo(importRef);
        await sleep(600);
        await clickPulse();
        setImportPressed(true);
        await sleep(160);
        setImportPressed(false);
        await sleep(200);
        setModalOpen(false);
        setNewFrom(EXISTING.length);
        setRows(EXISTING.concat(IMPORT_IDX.map((i) => ({ icon: "pdf", t: ZITEMS[i], y: "â", a: "â", src: true }))));
        await sleep(3200);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [inView]);

  return (
    <div ref={rootRef} style={styles.frame}>
      {/* tabs */}
      <div style={styles.tabs}>
        <div style={styles.tabActive}>My Uploads</div>
        <div style={styles.tab}>Saved Abstract</div>
      </div>

      {/* toolbar */}
      <div style={styles.toolbar}>
        <span style={{ fontSize: 9.5, color: C.body }}>Formats : PDF, DOC, DOCX, XLSX, CSV</span>
        <span style={{ ...styles.tbBtn, color: C.blue }}><Bookmark size={11} color={C.blue} />Add To Paper Insights</span>
        <span style={{ ...styles.tbBtn, color: C.teal }}><Upload size={11} color={C.teal} />Upload</span>
        <motion.span ref={zbtnRef} animate={{ scale: zbtnPressed ? 0.93 : 1 }} transition={{ duration: 0.15 }} style={styles.tbBtn}>
          <BookOpen size={11} color={C.zoteroRed} />Import from Zotero
        </motion.span>
        <span style={styles.tbBtn}><BookOpen size={11} color={C.zoteroRed} />Import from Mendeley</span>
      </div>

      {/* table */}
      <div style={styles.table}>
        <div style={styles.thead}>
          <span style={{ width: 22 }}><span style={{ ...styles.ck, borderColor: "rgba(255,255,255,0.6)" }} /></span>
          <span style={{ width: 18 }}>#</span>
          <span style={{ flex: 2.4 }}>TITLE</span>
          <span style={{ width: 44 }}>YEAR</span>
          <span style={{ flex: 1 }}>AUTHORS</span>
          <span style={{ width: 44, textAlign: "center" }}>Source</span>
          <span style={{ width: 60 }}>ADDED</span>
          <span style={{ width: 52, textAlign: "center" }}>REMOVE</span>
          <span style={{ width: 66 }}>Not Added</span>
        </div>
        <div style={styles.tbody}>
          {rows.map((r, i) => (
            <Row key={i + r.t} r={r} n={i + 1} isNew={i >= newFrom} />
          ))}
        </div>
      </div>

      {/* Zotero modal */}
      <div style={{ ...styles.backdrop, opacity: modalOpen ? 1 : 0, pointerEvents: modalOpen ? "auto" : "none" }} />
      <div style={{ ...styles.modal, opacity: modalOpen ? 1 : 0, pointerEvents: modalOpen ? "auto" : "none", transform: `translate(-50%, ${modalOpen ? "-50%" : "-46%"})` }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
          <span style={{ fontSize: 22, fontWeight: 800, color: C.ink, letterSpacing: "-0.5px" }}>
            <span style={{ color: C.zoteroRed }}>z</span>otero
          </span>
          <span style={styles.disconnect}>Disconnect</span>
        </div>

        <div style={{ display: "flex", justifyContent: "center", gap: 12 }}>
          <motion.span ref={fetchRef} animate={{ scale: fetchPressed ? 0.93 : 1 }} transition={{ duration: 0.15 }} style={{ ...styles.fetchBtn, background: C.blue }}>
            <Layers size={13} />Fetch Papers From Library
          </motion.span>
          <span style={{ ...styles.fetchBtn, background: C.green }}>
            <LibraryIcon size={13} />Fetch Collections
          </span>
        </div>

        {fetchView === "loading" && (
          <div style={{ display: "flex", justifyContent: "center", padding: 26 }}>
            <motion.span animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} style={styles.spinner} />
          </div>
        )}

        {fetchView === "list" && (
          <div style={{ marginTop: 12 }}>
            <div style={styles.searchBox}>
              <Search size={11} color={C.hint} />
              <span style={{ fontSize: 10, color: C.hint }}>Search files â¦</span>
            </div>
            <div style={styles.itemsList}>
              {ZITEMS.map((f, i) => (
                <div key={i} ref={(el) => (itemRefs.current[i] = el)} style={styles.item}>
                  <span style={{ width: 16, color: C.muted }}>{i + 9}.</span>
                  <span style={{ ...styles.ck, ...(checked.includes(i) ? styles.ckOn : {}) }}>
                    {checked.includes(i) && <Check size={10} color="#fff" />}
                  </span>
                  <FileText size={13} color={C.blue} />
                  {f}
                </div>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
              <motion.span ref={importRef} animate={{ scale: importPressed ? 0.93 : 1 }} transition={{ duration: 0.15 }} style={styles.importBtn}>
                Import Selected
              </motion.span>
            </div>
          </div>
        )}
      </div>

      {/* fake cursor */}
      <div style={{ ...styles.cursor, transform: `translate(${cursor.x}px, ${cursor.y}px)` }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))" }}>
          <path d="M4 2 L4 20 L9 15 L12.5 22 L15 21 L11.5 14 L18 14 Z" fill="#1e293b" stroke="#fff" strokeWidth="1.2" strokeLinejoin="round" />
        </svg>
        <motion.span
          initial={false}
          animate={cursor.click ? { scale: [0, 1.8], opacity: [0.5, 0] } : { scale: 0, opacity: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          style={styles.clickRipple}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles â no sidebar, fills full container width
// ---------------------------------------------------------------------------
const styles = {
  frame: {
    position: "relative",
    width: "100%",
    height: 470,
    borderRadius: 14,
    overflow: "hidden",
    background: "#fff",
    border: `1px solid ${C.cardBorder}`,
    boxShadow: "0 14px 44px rgba(30,41,59,0.14)",
    fontFamily: "'Inter', system-ui, sans-serif",
    display: "flex",
    flexDirection: "column",
  },

  tabs: { display: "flex", borderBottom: `1px solid ${C.borderSoft}` },
  tab: { flex: 1, textAlign: "center", padding: 11, fontSize: 11, color: C.muted },
  tabActive: { flex: 1, textAlign: "center", padding: 11, fontSize: 11, color: C.ink, fontWeight: 600, borderBottom: `2px solid ${C.ink}` },

  toolbar: { display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", flexWrap: "wrap" },
  tbBtn: { display: "flex", alignItems: "center", gap: 5, fontSize: 9.5, color: C.slate, background: "#f1f3f7", borderRadius: 7, padding: "6px 9px" },

  table: { flex: 1, margin: "0 14px 12px", border: `1px solid ${C.borderSoft}`, borderRadius: 10, overflow: "hidden", display: "flex", flexDirection: "column" },
  thead: { display: "flex", alignItems: "center", gap: 6, background: C.teal, color: "#fff", fontSize: 9, fontWeight: 600, padding: "10px 12px" },
  tbody: { flex: 1, overflow: "hidden" },
  ck: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 14, height: 14, borderRadius: 3, border: `1.5px solid ${C.hint}`, flexShrink: 0 },
  ckOn: { background: C.blue, borderColor: C.blue },
  trashBtn: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 22, height: 19, background: "#fdecec", borderRadius: 5 },
  notAdded: { fontSize: 8, color: "#64748b", background: "#eceef2", borderRadius: 6, padding: "4px 7px" },

  backdrop: { position: "absolute", inset: 0, background: "rgba(30,41,59,0.35)", zIndex: 20, transition: "opacity 0.3s ease" },
  modal: { position: "absolute", top: "50%", left: "50%", zIndex: 21, width: "70%", background: "#fff", borderRadius: 14, boxShadow: "0 20px 50px rgba(30,41,59,0.25)", padding: "18px 20px", transition: "opacity 0.3s ease, transform 0.3s ease" },
  disconnect: { marginLeft: "auto", fontSize: 10, color: "#fff", background: C.red, borderRadius: 6, padding: "5px 12px" },
  fetchBtn: { display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#fff", borderRadius: 8, padding: "9px 16px" },
  spinner: { width: 26, height: 26, border: "3px solid #d7f0ea", borderTopColor: C.teal, borderRadius: "50%" },
  searchBox: { display: "flex", alignItems: "center", gap: 6, background: C.field, border: `1px solid ${C.borderSoft}`, borderRadius: 8, padding: "7px 10px", marginBottom: 8 },
  itemsList: { maxHeight: 150, overflow: "hidden", display: "flex", flexDirection: "column" },
  item: { display: "flex", alignItems: "center", gap: 9, padding: "8px 4px", fontSize: 10.5, color: C.slate },
  importBtn: { fontSize: 11, color: "#fff", background: C.blue, borderRadius: 8, padding: "8px 18px", fontWeight: 600 },

  cursor: { position: "absolute", top: 0, left: 0, width: 20, height: 20, zIndex: 60, pointerEvents: "none", transition: "transform 0.7s cubic-bezier(0.22, 0.61, 0.36, 1)" },
  clickRipple: { position: "absolute", top: 2, left: 2, width: 16, height: 16, borderRadius: "50%", background: "rgba(47,109,246,0.4)" },
};
